/**
 * Closed-world primitive validators shared by every game kind's spec parser.
 * Each kind owns its own field list; only these primitive shapes are shared.
 */
export class GameSpecValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "GameSpecValidationError";
  }
}

export function record(value: unknown, path: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new GameSpecValidationError(`${path} must be an object`);
  }
  return value as Record<string, unknown>;
}

export function exactKeys(value: Record<string, unknown>, keys: readonly string[], path: string): void {
  for (const key of Object.keys(value)) {
    if (!keys.includes(key)) throw new GameSpecValidationError(`${path}.${key} is not allowed`);
  }
  for (const key of keys) {
    if (!(key in value)) throw new GameSpecValidationError(`${path}.${key} is required`);
  }
}

/** Like exactKeys, but with a second list of keys that may be absent. Unknown keys still reject. */
export function boundedKeys(value: Record<string, unknown>, required: readonly string[], optional: readonly string[], path: string): void {
  for (const key of Object.keys(value)) {
    if (!required.includes(key) && !optional.includes(key)) throw new GameSpecValidationError(`${path}.${key} is not allowed`);
  }
  for (const key of required) {
    if (!(key in value)) throw new GameSpecValidationError(`${path}.${key} is required`);
  }
}

/**
 * Validates an optional emoji glyph: short, starts with a pictographic
 * character, and contains only emoji-family code points (modifiers,
 * variation selector, ZWJ sequences). Security does not depend on this --
 * glyphs are rendered via fillText/textContent and JSON-escaped like any
 * other string -- this is a visual-sanity contract so a "glyph" can never
 * be a word of prose (rule 15) or an invisible string.
 */
export function glyph(value: unknown, path: string): string | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== "string") throw new GameSpecValidationError(`${path} must be an emoji`);
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 8) throw new GameSpecValidationError(`${path} must be a single emoji`);
  if (!/^\p{Extended_Pictographic}(?:[\u{FE0F}\u{200D}\p{Emoji_Modifier}]|\p{Extended_Pictographic})*$/u.test(trimmed)) {
    throw new GameSpecValidationError(`${path} must be a single emoji`);
  }
  return trimmed;
}

export function text(value: unknown, path: string, min: number, max: number): string {
  if (typeof value !== "string" || value.trim().length < min || value.length > max) {
    throw new GameSpecValidationError(`${path} must be ${min}-${max} characters`);
  }
  return value;
}

export function integer(value: unknown, path: string, min: number, max: number): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < min || value > max) {
    throw new GameSpecValidationError(`${path} must be an integer from ${min} to ${max}`);
  }
  return value;
}

export function color(value: unknown, path: string): string {
  if (typeof value !== "string" || !/^#[0-9a-fA-F]{6}$/.test(value)) {
    throw new GameSpecValidationError(`${path} must be a six-digit hex color`);
  }
  return value;
}

export function bool(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") throw new GameSpecValidationError(`${path} must be true or false`);
  return value;
}

/** Validates a fixed-length tuple of distinct, bounded-length strings (quiz answers, etc). */
export function textArray(value: unknown, path: string, count: number, min: number, max: number): string[] {
  if (!Array.isArray(value) || value.length !== count) {
    throw new GameSpecValidationError(`${path} must contain exactly ${count} entries`);
  }
  return value.map((entry, index) => text(entry, `${path}[${index}]`, min, max));
}
